import { useMemo, useState } from "react";
import { ArrowRight, TrendingUp } from "lucide-react";
import { Reveal, SectionHead } from "./shared";

const fmt = (n) => "$" + Math.round(n).toLocaleString();

const Slider = ({ label, value, set, min, max, step = 1, display, testid }) => (
    <div>
        <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-slate-300 font-medium">{label}</label>
            <span className="ff-mono text-sm text-cyan-300 font-semibold">{display}</span>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => set(Number(e.target.value))}
            data-testid={testid}
            aria-label={label}
        />
    </div>
);

export default function RoiCalculator() {
    const [leads, setLeads] = useState(120);
    const [value, setValue] = useState(1200);
    const [conv, setConv] = useState(25);
    const [missed, setMissed] = useState(40);

    const { lostMonthly, lostAnnual, opportunity } = useMemo(() => {
        const missedLeads = leads * (missed / 100);
        const lostDeals = missedLeads * (conv / 100);
        const lostMonthly = lostDeals * value;
        return {
            lostMonthly,
            lostAnnual: lostMonthly * 12,
            opportunity: lostMonthly * 0.5,
        };
    }, [leads, value, conv, missed]);

    return (
        <section id="roi" className="py-24 sm:py-32 relative overflow-hidden" data-testid="roi-section">
            <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-cyan-500/[0.06] blur-[140px] rounded-full pointer-events-none" />
            <div className="container-x relative">
                <SectionHead
                    eyebrow="08 — The Real Cost"
                    title="How Much Could You Be Losing From Missed Leads?"
                    sub="No email required. Just honest math."
                    testid="roi-heading"
                />
                <div className="mt-16 grid lg:grid-cols-2 gap-6 items-stretch">
                    <Reveal>
                        <div className="glass rounded-3xl p-8 sm:p-10 h-full flex flex-col gap-8" data-testid="roi-inputs">
                            <Slider label="Monthly Leads" value={leads} set={setLeads} min={10} max={500} step={5} display={`${leads} leads`} testid="roi-slider-leads" />
                            <Slider label="Average Customer Value" value={value} set={setValue} min={100} max={10000} step={50} display={fmt(value)} testid="roi-slider-value" />
                            <Slider label="Current Conversion Rate" value={conv} set={setConv} min={5} max={60} display={`${conv}%`} testid="roi-slider-conversion" />
                            <Slider label="Missed / Unanswered Leads" value={missed} set={setMissed} min={10} max={80} display={`${missed}%`} testid="roi-slider-missed" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="rounded-3xl border border-cyan-400/25 bg-gradient-to-b from-cyan-400/[0.08] to-transparent p-8 sm:p-10 h-full flex flex-col" data-testid="roi-results">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-10 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-cyan-300" />
                                </div>
                                <p className="ff-mono text-xs uppercase tracking-[0.2em] text-cyan-300">Potential Revenue You're Leaving Behind</p>
                            </div>
                            <p className="ff-display text-5xl sm:text-6xl font-black text-white tracking-tight" data-testid="roi-lost-monthly">
                                {fmt(lostMonthly)}
                                <span className="text-lg text-slate-500 font-medium"> /mo</span>
                            </p>
                            <p className="mt-4 text-slate-400 text-sm" data-testid="roi-lost-annual">
                                That's <span className="text-white font-semibold">{fmt(lostAnnual)}</span> slipping away every year.
                            </p>
                            <div className="mt-8 pt-8 border-t border-white/10">
                                <p className="ff-mono text-xs uppercase tracking-[0.2em] text-emerald-300 mb-2">Automation Opportunity</p>
                                <p className="text-slate-300 text-sm leading-relaxed" data-testid="roi-opportunity">
                                    Recovering even half of those missed leads means an extra{" "}
                                    <span className="text-emerald-300 font-bold">{fmt(opportunity)}/month</span> — without hiring anyone.
                                </p>
                            </div>
                            <a href="#book" className="btn-primary mt-auto pt-0 mt-10 w-fit" data-testid="roi-cta-button">
                                Calculate My Opportunity
                                <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
